import {getEnvironment} from "../lib/get-environment";

/**
 * Identity Provider client config (per-environment).
 *
 * Provider-agnostic shape — Auth0 today, Zitadel migration is a config swap
 * plus an SDK swap inside `useIdpClient.ts`. The `provider` field disambiguates
 * provider-specific behavior at the SDK boundary.
 *
 * @module client/Auth/idp-config
 */

export type IdpProvider = 'auth0' | 'zitadel';

export interface IdpConfig {
    provider: IdpProvider;
    audience: string;
    domain: string;
    clientId: string;
    callbackUrl: string;
    afterLogout: string;
}

const IDP_CONFIG_DEV: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth-dev.outlawpractice.com',
    clientId: 'ifJPp6HM7WuvhEYuoUSoPb62t16Y7hdF',
    callbackUrl: `${window.location.protocol}//${window.location.host}/callback`,
    afterLogout: `${window.location.protocol}//${window.location.host}/`,
};

const IDP_CONFIG_TEST: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth-test.outlawpractice.com',
    clientId: 'CUIGoH4kOIShUfbifW7AKaDPHaUvkr8B',
    callbackUrl: 'https://app.outlawpractice.dev/callback',
    afterLogout: 'https://app.outlawpractice.dev/',
};

const IDP_CONFIG_DEMO: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth-test.outlawpractice.com',
    clientId: 'CUIGoH4kOIShUfbifW7AKaDPHaUvkr8B',
    callbackUrl: 'https://app-demo.outlawpractice.com/callback',
    afterLogout: 'https://app-demo.outlawpractice.com/',
};

const IDP_CONFIG_BETA: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth.outlawpractice.com',
    clientId: 'cGMEchitYrYfM3uRMgBGvFIYtoOSc9ks',
    callbackUrl: `${window.location.protocol}//${window.location.host}/callback`,
    afterLogout: `${window.location.protocol}//${window.location.host}/`,
};

const IDP_CONFIG_PROD: IdpConfig = {
    provider: 'auth0',
    audience: 'https://api.outlawpractice.com',
    domain: 'auth.outlawpractice.com',
    clientId: 'cGMEchitYrYfM3uRMgBGvFIYtoOSc9ks',
    callbackUrl: 'https://app.outlawpractice.com/callback',
    afterLogout: 'https://app.outlawpractice.com/',
};

/**
 * Zitadel for the TS client against the Rust practice server (OWA-1137): the Outlaw Dev instance,
 * its Practice project (the access token's audience) and its "Practice Web" PKCE app. Development
 * and test share this one instance (John, 2026-10-12, q-20261012-225632-iuo0). The TS server stays
 * on Auth0, so this applies only when the client talks to the Rust server.
 */
const IDP_CONFIG_ZITADEL: IdpConfig = {
    provider: 'zitadel',
    audience: '394220269989789953',
    domain: 'auth-dev.outlaw.app',
    clientId: '394846861241876737',
    callbackUrl: `${window.location.protocol}//${window.location.host}/callback`,
    afterLogout: `${window.location.protocol}//${window.location.host}/`,
};

/** Explicit build selection. Customer tiers require their own profile below. */
export function developmentProvider(env: Record<string, unknown> = import.meta.env): IdpProvider {
    return env.VITE_IDP_PROVIDER === 'zitadel' ? 'zitadel' : 'auth0';
}

/** Public OIDC metadata supplied from the selected customer deployment profile.
 * No credentials belong in these VITE_ fields. Missing metadata refuses the
 * selected cutover rather than borrowing auth-dev or silently retaining Auth0. */
function customerZitadelConfig(): IdpConfig {
    const issuer = import.meta.env.VITE_ZITADEL_ISSUER;
    const clientId = import.meta.env.VITE_ZITADEL_CLIENT_ID;
    const projectId = import.meta.env.VITE_ZITADEL_PROJECT_ID;
    if (!issuer || !clientId?.trim() || !projectId?.trim()) {
        throw new Error('Customer Zitadel requires issuer, client ID and project ID build metadata');
    }
    const url = new URL(issuer);
    if (url.protocol !== 'https:' || url.username || url.password ||
        url.pathname !== '/' || url.search || url.hash) {
        throw new Error('Customer Zitadel issuer must be an HTTPS origin');
    }
    return {
        provider: 'zitadel', domain: url.host, clientId, audience: projectId,
        callbackUrl: `${window.location.origin}/callback`, afterLogout: `${window.location.origin}/`,
    };
}

/** A host where the development Zitadel instance (auth-dev) may be used. */
export function isDevelopmentHost(hostname: string): boolean {
    const host = hostname.toLowerCase();
    return ['localhost', '127.0.0.1', '[::1]', '::1', 'dev.outlawpractice.com'].includes(host)
        || host.endsWith('.localhost') || host.endsWith('.outlaw.local');
}

let IDP_CONFIG: IdpConfig;

switch (getEnvironment()) {
    case 'P':
        IDP_CONFIG = developmentProvider() === 'zitadel' ? customerZitadelConfig() : IDP_CONFIG_PROD;
        break;
    case 'B':
        IDP_CONFIG = developmentProvider() === 'zitadel' ? customerZitadelConfig() : IDP_CONFIG_BETA;
        break;
    case 'DM':
        IDP_CONFIG = IDP_CONFIG_DEMO;
        break;
    case 'T':
        IDP_CONFIG = developmentProvider() === 'zitadel' ? IDP_CONFIG_ZITADEL : IDP_CONFIG_TEST;
        break;
    default:
        // An unknown host also lands here (getEnvironment falls back to development). With
        // Zitadel selected it must not borrow auth-dev: only a development host may.
        if (developmentProvider() === 'zitadel' && !isDevelopmentHost(window.location.hostname)) {
            throw new Error(`Zitadel sign-in is not configured for ${window.location.hostname}`);
        }
        IDP_CONFIG = developmentProvider() === 'zitadel' ? IDP_CONFIG_ZITADEL : IDP_CONFIG_DEV;
}

export default IDP_CONFIG;

// Backward-compatible aliases (to be removed once all importers move to IdpConfig/IDP_CONFIG).
export type AuthConfig = IdpConfig;
